export const HANDBOOK_SECTIONS = [
  {
    id: 'roles',
    title: 'The three roles',
    summary: 'Clients book and pay. Vendors deliver the event. Admin sets the markets, tax, and when money is released.',
    blocks: [
      {
        type: 'p',
        text: 'This is a marketplace, not a shop that sells photo-booth time itself. The vendor supplies the service. The platform runs discovery, booking, checkout, tax records, commission, refunds, and vendor payouts.',
      },
      {
        type: 'table',
        headers: ['Role', 'What they do', 'What they never do'],
        rows: [
          ['Client', 'Search vendors, request a booking, pay the marketplace at checkout, leave reviews.', 'They do not pay the vendor bank account directly for a marketplace booking.'],
          ['Vendor', 'Verify the business, publish services, run events, see held and available earnings, complete a tax profile.', 'They do not collect marketplace tax and keep it. Tax is a platform liability until it is remitted.'],
          ['Admin', 'Turn markets on or off, set commission, review vendors, refund, record or send payouts, export tax reports.', 'Admin does not invent a tax rate in a booking. Rates come from the market’s tax engine.'],
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Vendor subscriptions (the plan a vendor pays to use the software) are a separate income stream from booking commission. Subscription money is not vendor earnings and is not paid back out.',
      },
    ],
  },
  {
    id: 'client',
    title: 'Client journey',
    blocks: [
      {
        type: 'ul',
        items: [
          'Browse vendors and services. A market must be Active and open for bookings before a client can book there.',
          'Choose a package, then enter event details. The event date must be after today.',
          'Event market and billing market are chosen from active bookable markets. Those fields drive tax. A browser location is only a hint.',
          'Checkout charges the platform Stripe account: vendor service + platform fee + tax when that market collects tax.',
          'The client can pay with a card Stripe accepts, including cards used from Africa. The charge still lands on the marketplace Stripe account.',
          'After the event, the client can review the vendor. Refunds, when issued by admin, reverse the fee and the tax on that payment.',
        ],
      },
      {
        type: 'p',
        text: 'Private bookings a vendor records themselves (offline / vendor-manual) are not marketplace money. They stay off the platform ledger, tax remittance report, and payout balance.',
      },
    ],
  },
  {
    id: 'vendor',
    title: 'Vendor journey',
    blocks: [
      {
        type: 'ul',
        items: [
          'Sign up, pick a subscription plan, then complete verification.',
          'Operating market is an admin market with status Active. The Countries catalog is not that list. Draft and paused markets do not appear.',
          'City and state stay required. ZIP / postal is optional when the operating market is an African country, because many of those countries do not use postal codes.',
          'Tax residence and payout market are the same kind of list: active markets.',
          'The vendor publishes services, packages, portfolio, and service areas inside those markets.',
          'When a client books, earnings are held. They become available only after the booking is completed and the market’s hold window has passed, and only if payouts are not frozen.',
          'The vendor payout page shows held, available, and already sent amounts in the catalog currency (typically USD), not the Stripe settlement currency.',
        ],
      },
    ],
  },
  {
    id: 'admin',
    title: 'Admin journey',
    blocks: [
      {
        type: 'table',
        headers: ['Admin area', 'Use it for'],
        rows: [
          ['Countries', 'Reference catalog: names, flags, dial codes. Creating a country does not open that country for business.'],
          ['Markets', 'Where the marketplace operates. Status, bookings, payouts, tax collection, tax module, and payment rail.'],
          ['Vendors', 'Verification, plan usage, tax profile, Stripe Connect status, earnings summary.'],
          ['Bookings', 'Every marketplace order, event date, and linked payments.'],
          ['Payments', 'Client charges on the platform Stripe account.'],
          ['Refunds', 'Refund a charge. Fee and tax journal lines reverse. The booking can be frozen so it is not paid out.'],
          ['Vendor payouts', 'What each vendor has earned, what is held, what is available, and what has been sent.'],
          ['Accounting', 'Platform view of fees, tax, and amounts still owed.'],
          ['Tax reports', 'CRA, 1099-K prep, DAC7 completeness, and tax remittance (open vs reversed tax by jurisdiction).'],
          ['Settings', 'Commission, settlement currency, site content, and branding.'],
        ],
      },
    ],
  },
  {
    id: 'markets',
    title: 'Countries and markets',
    blocks: [
      {
        type: 'p',
        text: 'Countries and markets are different lists on purpose. Countries is the world catalog used when an admin creates a market. Markets is the operating list. Vendor verification, vendor tax, checkout event market, and checkout billing market all read Markets. Adding France under Countries does nothing until a France market exists and is Active.',
      },
      {
        type: 'table',
        headers: ['Market status', 'Who can select it', 'Bookings'],
        rows: [
          ['Draft', 'Nobody in vendor or checkout forms', 'Off'],
          ['Active, bookings on', 'Vendors and clients', 'On'],
          ['Active, bookings off', 'Vendors only', 'Off for new client bookings'],
          ['Paused', 'Hidden from vendor and checkout pickers', 'Off until you set it Active again'],
        ],
      },
      {
        type: 'p',
        text: 'Client checkout is stricter than vendor setup: the event market and billing market must be active and marked open for bookings. Vendor operating market, tax residence, and payout market only require Active.',
      },
    ],
  },
  {
    id: 'tax',
    title: 'How tax works',
    blocks: [
      {
        type: 'p',
        text: 'Tax is collected from the client at checkout when the booking’s market has tax collection turned on. It is stored as a liability. It is not platform revenue and it is not part of the vendor’s payout.',
      },
      {
        type: 'h',
        text: 'What decides the tax',
      },
      {
        type: 'ul',
        items: [
          'The active market, chosen from the event market and billing market the client confirms. IP address is not the tax address.',
          'The market’s tax module (for example Canada GST/HST, US sales tax, Ghana VAT, EU VAT, Nigeria VAT, South Africa VAT, or none).',
          'The tax engine on that market: none (records zero), a configured rate, or Stripe Tax when that provider is connected.',
          'Vendor tax profile fields the market requires, such as legal name, VAT registered, or GST/HST registered.',
          'Customer type (individual or business) and a business tax ID when the market asks for them.',
        ],
      },
      {
        type: 'h',
        text: 'What a completed payment records',
      },
      {
        type: 'ul',
        items: [
          'A tax amount on the payment and on the booking.',
          'One or more booking tax lines: jurisdiction, name, rate, taxable amount, tax amount, currency, engine, and status open.',
          'Ledger lines: vendor earning, platform fee, and tax liability. Tax liability is money you may have to remit. It is not income.',
          'If the market’s engine is “none”, the tax line is zero. Turning on tax collection without an engine does not invent a rate.',
        ],
      },
      {
        type: 'h',
        text: 'Installments',
      },
      {
        type: 'p',
        text: 'If the client pays a deposit, tax on that payment is the same share of the booking tax as the payment is of the booking total. A 50% deposit carries 50% of the tax. The vendor’s share of that payment is reduced by that tax slice, not by the whole booking’s tax.',
      },
      {
        type: 'h',
        text: 'Refunds',
      },
      {
        type: 'p',
        text: 'A refund marks the tax lines reversed and posts reversing ledger entries for the fee, the tax, and the vendor earning. If a Stripe Connect payout for that money was already sent, the refund response flags that a clawback may be needed. Partial refund amounts are not a separate workflow yet.',
      },
      {
        type: 'h',
        text: 'Remittance',
      },
      {
        type: 'p',
        text: 'Finance → Tax reports → Tax remittance sums open tax lines and subtracts reversed lines for a date range and an optional market. That number is what the marketplace still owes a tax authority, not what a vendor is owed. There is no “mark as remitted” button yet, so paying the authority is still an offline step you track beside this report.',
      },
      {
        type: 'note',
        tone: 'warn',
        text: 'Who is legally the supplier, which body you file with, and whether you are a marketplace facilitator are counsel decisions. The software records the split. It does not decide the filing.',
      },
    ],
  },
  {
    id: 'money-split',
    title: 'How a payment splits',
    blocks: [
      {
        type: 'p',
        text: 'The client’s charge, in catalog currency, is split three ways. Catalog currency is what the price list uses (often USD). Stripe may settle the platform account in another currency, such as CAD. Payout math uses the catalog amount so a vendor is not paid the FX-converted Stripe total.',
      },
      {
        type: 'table',
        headers: ['Piece', 'Where it goes', 'Formula'],
        rows: [
          ['Vendor earning', 'Held, then paid to the vendor', 'Client amount − platform fee − tax'],
          ['Platform fee', 'Marketplace revenue', 'Commission setting (percent or fixed), added at checkout'],
          ['Tax', 'Tax liability until remitted', 'Engine result for that market, or zero'],
        ],
      },
      {
        type: 'p',
        text: 'Example: service 2,000, fee 100, tax 150. The client pays 2,250. Vendor earning is 2,000. Platform revenue is 100. Tax liability is 150. Processor fees Stripe charges the platform are a platform cost, not a deduction the vendor ledger currently itemizes on every payout.',
      },
    ],
  },
  {
    id: 'payouts',
    title: 'How vendors get paid',
    blocks: [
      {
        type: 'p',
        text: 'Vendors are not paid the moment a client checks out. The cash sits with the marketplace. The vendor balance moves through three stages.',
      },
      {
        type: 'table',
        headers: ['Stage', 'Meaning'],
        rows: [
          ['Held', 'Payment succeeded, but the event is not completed, the market hold days have not passed, the market payout policy is manual only, or payouts are frozen.'],
          ['Available', 'Released share minus amounts already sent. This is what can be paid now.'],
          ['Sent', 'A disbursement row exists: either a recorded manual payment or a Stripe Connect transfer.'],
        ],
      },
      {
        type: 'h',
        text: 'Two ways money leaves the platform',
      },
      {
        type: 'ul',
        items: [
          'Stripe Connect transfer. Works only when the vendor finished Express onboarding and their payout country is one Stripe Connect supports. Admin can send it from Vendor payouts, and a scheduled job can send released balances when settlement settings allow it.',
          'Manual disbursement. Admin sends money outside Stripe (bank transfer, mobile money, or a local processor) and records the amount on Vendor payouts. The ledger then treats that amount as sent so the vendor is not paid twice.',
        ],
      },
      {
        type: 'p',
        text: 'A dispute or refund can freeze payouts on the vendor or the booking. A paid Connect transfer can be reversed from the disbursement record. A manual payment cannot be pulled back through Stripe; you recover it operationally and delete or reverse the recorded row if it was entered in error and was not a paid Connect transfer.',
      },
    ],
  },
  {
    id: 'africa',
    title: 'Africa: clients can pay, vendors often cannot connect',
    blocks: [
      {
        type: 'p',
        text: 'Stripe can charge a card for the marketplace even when the cardholder is in Africa, because the charge is on the platform’s Stripe account. Stripe Connect, which would let a vendor open their own merchant account and receive a transfer, is not offered for most African countries. Ghana and Nigeria are not Connect countries in this app. South Africa is on the Connect list the code accepts, but Stripe’s own account country and onboarding rules still decide whether a live ZA account can be created.',
      },
      {
        type: 'p',
        text: 'So a client in Accra can pay a Ghana vendor’s booking on Stripe. The Ghana vendor cannot click “connect Stripe” and receive that money into a Ghanaian Stripe account. Their earning still accrues on the internal ledger. Until a local payout rail exists, an admin pays them outside Stripe and records a manual disbursement.',
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Tax does not depend on Connect. The tax the client paid stays a platform liability for the market, whether the vendor is paid by Connect, by a local transfer, or not yet.',
      },
    ],
  },
  {
    id: 'paystack',
    title: 'Whether to add Paystack or another local payout partner',
    blocks: [
      {
        type: 'p',
        text: 'The seamless design is one checkout and two payout rails. Clients always pay the marketplace on Stripe, in every market, so tax, refunds, and the ledger stay in one place. Vendors are paid from that ledger by whichever rail their market can actually use.',
      },
      {
        type: 'table',
        headers: ['Option', 'What it would do', 'Recommendation'],
        rows: [
          [
            'Keep today’s manual payout',
            'Admin wires or sends mobile money, then records it under Vendor payouts.',
            'Correct for now. It already prevents double-pay. It does not scale and it is easy to pay the wrong person if ops is loose.',
          ],
          [
            'Replace checkout with Paystack in Africa',
            'African clients would pay Paystack instead of Stripe. Tax, refunds, and FX would split across two checkouts.',
            'Do not do this. Clients can already pay Stripe from Africa. A second checkout makes tax and refunds harder, not smoother.',
          ],
          [
            'Paystack (or Flutterwave / Hubtel) as payout only',
            'Stripe still collects every booking. A job sends the vendor share to a Ghana or Nigeria bank or mobile-money account and writes a disbursement the ledger understands.',
            'This is the path to add. Market payment rail “local PSP” should mean the payout rail, not a second checkout.',
          ],
          [
            'Stripe Connect where it exists',
            'US, Canada, UK, France, and other Connect countries get an automatic transfer of the released balance.',
            'Keep. South Africa only if Stripe will actually onboard that vendor on the live platform account.',
          ],
        ],
      },
      {
        type: 'h',
        text: 'What “seamless” looks like when the local partner is added',
      },
      {
        type: 'ul',
        items: [
          'Vendor in an African market still verifies, still files a tax profile, and still sees held and available earnings in USD (or the market display currency).',
          'They add a local payout account (bank or mobile money) instead of a Stripe Connect button when Connect is unavailable.',
          'After the event is completed and the hold passes, the platform sends only the vendor share. Tax and the platform fee stay in the marketplace accounts.',
          'The disbursement is stored with a provider name such as paystack, the partner’s transfer id, and status paid or failed. Refunds after that payout create the same clawback warning Connect refunds already create.',
          'Tax reports do not change. Remittance is still Finance → Tax reports, paid to the authority by the marketplace, not by routing tax through the vendor.',
        ],
      },
      {
        type: 'note',
        tone: 'warn',
        text: 'Paystack is not connected in this product yet. Choosing it, Flutterwave, or a Ghana specialist such as Hubtel is an ops and licensing decision: who holds the local bank account, which currency you convert from the Stripe settlement balance, and which payout names your counsel accepts. The ledger is already shaped to record that partner; the transfer call is the piece still to build.',
      },
    ],
  },
  {
    id: 'operating',
    title: 'Day-to-day operating rules',
    blocks: [
      {
        type: 'ul',
        items: [
          'Launch a country by creating a market from the catalog, filling tax and payout policy, then setting status to Active. Leave tax collection off until the engine for that market is real.',
          'Do not pay a vendor from Payments. Pay them from Vendor payouts, and only up to the available balance.',
          'Do not treat the Stripe payout to your platform bank as vendor money. Part of it is fee, part of it is tax, part of it is still held for events that have not happened.',
          'After you pay a tax authority, keep the remittance export for that period. The screen shows what was collected and what was reversed. It does not yet mark those lines as filed.',
          'If Stripe Connect shows an error for a Ghana or Nigeria vendor, that is expected. Use a recorded manual payout until a local payout partner is switched on for that market.',
        ],
      },
    ],
  },
];
